import { useCallback, useMemo, useRef, useState } from 'react'

import { toYType } from 'features/utils/useYjsState'
import { useIsFieldEditable } from 'features/views/attributes/editable/hooks/useIsFieldEditable'
import { getRecordDiff } from 'features/views/RecordManager/hooks/useRecordManagerState'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'
import { isCmdOrCtrlPressed } from 'utils/isCmdOrCtrlPressed'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

type EditableField = FieldDto & {
    isReadOnly: boolean
}

type UseEditableSectionStateProps = {
    fields: FieldDto[]
    onOpenChange: (open: boolean) => void
}

export function useEditableSectionEditableFieldsState({
    fields,
    onOpenChange,
}: UseEditableSectionStateProps) {
    const { record, replaceAttribute, saveChanges } = useRecordManagerContext()

    const isAttributeEditable = useIsFieldEditable()
    const editableFields: EditableField[] = useDeepEqualsMemoValue(
        fields.map((field) => {
            const isEditable = isAttributeEditable(field)

            return {
                ...field,
                isReadOnly: !isEditable,
            }
        })
    )

    const updatedFields = useRef(new Set<string>())
    const [tempRecord, setTempRecord] = useState<RecordDto>(record || ({} as RecordDto))
    const tempRecordRef = useRef(tempRecord)
    tempRecordRef.current = tempRecord

    const isDirty = useMemo(
        () => Object.keys(getRecordDiff(record, tempRecord)).length > 0,
        [record, tempRecord]
    )
    const isDirtyRef = useRef(isDirty)
    isDirtyRef.current = isDirty

    const onChangeAttribute = useCallback((fieldApiName: string, value: unknown) => {
        updatedFields.current.add(fieldApiName)

        setTempRecord((prev) => ({
            ...prev,
            [fieldApiName]: value,
        }))
    }, [])

    const handleSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault()

            if (!isDirtyRef.current) return

            const changedFields = Array.from(updatedFields.current)
            const changedValues: [string, unknown][] = changedFields.map((fieldApiName) => [
                fieldApiName,
                tempRecordRef.current[fieldApiName],
            ])

            for (const [fieldApiName, value] of changedValues) {
                replaceAttribute(fieldApiName, toYType(value))
            }

            // Run in next frame to be sure that the record is updated first.
            return new Promise<void>(async (resolve) => {
                requestAnimationFrame(async () => {
                    await saveChanges()

                    onOpenChange(false)

                    resolve()
                })
            })
        },
        [onOpenChange, replaceAttribute, saveChanges]
    )

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLFormElement>) => {
            if (e.key === 'Enter' && isCmdOrCtrlPressed(e.nativeEvent)) {
                e.preventDefault()
                handleSubmit(e)
            }
        },
        [handleSubmit]
    )

    return {
        editableFields,
        tempRecord,
        onChangeAttribute,
        handleSubmit,
        isDirty,
        handleKeyDown,
    }
}
